import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { Provider, useDispatch } from 'react-redux';
import store from './store/store';
import { checkAuth } from './store/authSlice';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import Layout from './components/Layout/Layout';
import Dashboard from './pages/Dashboard/Dashboard';
import Instructions from './pages/Instructions/Instructions';
import Staff from './pages/Staff/Staff';
import Items from './pages/Items/Items';
import Conversations from './pages/Conversations/Conversations';
import Chatbot from './pages/Chatbot/Chatbot';
import Login from './pages/Login/Login';
import Signup from './pages/Signup/Signup';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import ResetPassword from './pages/ResetPassword/ResetPassword';
import Companies from './pages/Companies/Companies';
import Users from './pages/Users/Users';
import WhatsAppNumbers from './pages/WhatsAppNumbers/WhatsAppNumbers';
import CustomerSummary from './pages/CustomerSummary/CustomerSummary';
import SummaryInstructions from './pages/SummaryInstructions/SummaryInstructions';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#7b1fa2',
      light: '#9c27b0',
      dark: '#6a1b9a',
    },
    success: {
      main: '#388e3c',
      light: '#4caf50',
      dark: '#2e7d32',
    },
    error: {
      main: '#d32f2f',
      light: '#ef5350',
      dark: '#c62828',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 600,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

function AppContent() {
  const dispatch = useDispatch();
  // const loading = useSelector(selectAuthLoading);

  useEffect(() => {
    dispatch(checkAuth());
  }, [dispatch]);

  return (
    <Router>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/chatbot" element={<Chatbot />} />

        {/* Protected admin panel routes with layout */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Navigate to="/dashboard" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout><Dashboard /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/instructions"
          element={
            <ProtectedRoute>
              <Layout><Instructions /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff"
          element={
            <ProtectedRoute>
              <Layout><Staff /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/items"
          element={
            <ProtectedRoute>
              <Layout><Items /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/conversations"
          element={
            <ProtectedRoute>
              <Layout><Conversations /></Layout>
            </ProtectedRoute>
          }
        />

        {/* Super Admin Routes */}
        <Route
          path="/companies"
          element={
            <ProtectedRoute requireSuperAdmin={true}>
              <Layout><Companies /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute requireCompanyAdmin={true}>
              <Layout><Users /></Layout>
            </ProtectedRoute>
          }
        />        <Route 
          path="/whatsapp-numbers" 
          element={
            <ProtectedRoute requireCompanyAdmin={true}>
              <Layout><WhatsAppNumbers /></Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/customer-summary" 
          element={
            <ProtectedRoute requireCompanyAdmin={true}>
              <Layout><CustomerSummary /></Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/summary-instructions" 
          element={
            <ProtectedRoute requireCompanyAdmin={true}>
              <Layout><SummaryInstructions /></Layout>
            </ProtectedRoute>
          } 
        />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppContent />
      </ThemeProvider>
    </Provider>
  );
}

export default App;
